import React from 'react';
import { Link } from 'react-router-dom';
import { TESTIMONIALS, IMAGES } from '../lib/content';

// Positions where a feature image should slot into the grid.
// The image gets paired visually with the surrounding quote.
const FEATURE_IMAGE_AT = {
  1: IMAGES.gallery[2],
  4: IMAGES.gallery[5],
  7: IMAGES.gallery[8],
  10: IMAGES.gallery[1],
};

// Quotes that should render as a large, feature card (bigger italic serif)
const FEATURE_INDEXES = new Set([0, 3, 6, 9]);

export default function Testimonials() {
  return (
    <div>
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 md:pt-24 pb-10">
        <div className="font-label text-zb-accent mb-6">Kind words</div>
        <h1 className="font-serif text-5xl md:text-7xl text-zb-ink tracking-tight leading-[0.95]">
          How I <span className="italic text-zb-brand">linger,</span> long after.
        </h1>
        <p className="mt-8 text-zb-ink2 text-base md:text-lg max-w-2xl leading-relaxed">
          A collection of words left behind by clients who took the time to
          write. Every quote is a real, verified review from my{' '}
          <a
            href="https://scarletblue.com.au/escort/zahra-belle"
            target="_blank"
            rel="noreferrer"
            data-testid="testimonials-sb-link"
            className="text-zb-brand underline underline-offset-4 hover:text-zb-brandHover"
          >
            Scarlet Blue profile
          </a>
          . Unedited, in their own voices.
        </p>
      </section>

      {/* MAGAZINE MASONRY GRID */}
      <section className="max-w-6xl mx-auto px-6 md:px-12 pb-16 md:pb-20">
        <div className="columns-1 md:columns-2 gap-6 md:gap-8 [column-fill:_balance]">
          {TESTIMONIALS.map((t, i) => {
            const isFeature = FEATURE_INDEXES.has(i);
            const featureImg = FEATURE_IMAGE_AT[i];

            return (
              <React.Fragment key={i}>
                {featureImg && (
                  <div
                    className="break-inside-avoid mb-6 md:mb-8 zb-img-wrap aspect-[4/5]"
                    data-testid={`testimonial-feature-image-${i}`}
                  >
                    <img
                      src={featureImg}
                      alt=""
                      loading="lazy"
                    />
                  </div>
                )}
                <figure
                  data-testid={`testimonial-${i}`}
                  className={`break-inside-avoid mb-6 md:mb-8 zb-fade-up p-6 md:p-8 border border-zb-border ${
                    isFeature ? 'bg-zb-bg2' : 'bg-zb-bg'
                  }`}
                  style={{ animationDelay: `${(i % 6) * 0.06}s` }}
                >
                  <div className="font-label text-zb-accent mb-3 text-[10.5px] tracking-[0.28em]">
                    No. {String(i + 1).padStart(2, '0')}
                  </div>
                  <blockquote
                    className={`font-serif italic text-zb-ink ${
                      isFeature
                        ? 'text-2xl md:text-[32px] leading-[1.2]'
                        : 'text-lg md:text-xl leading-[1.4]'
                    }`}
                  >
                    &ldquo;{t.quote}&rdquo;
                  </blockquote>
                  <figcaption className="mt-5 pt-4 border-t border-zb-border/70 flex items-center justify-between">
                    <span className="font-serif text-zb-ink text-base md:text-lg">
                      {t.author}
                    </span>
                    {t.date && (
                      <span className="font-label text-zb-accent text-[11px] tracking-[0.22em]">
                        {t.date}
                      </span>
                    )}
                  </figcaption>
                </figure>
              </React.Fragment>
            );
          })}
        </div>
      </section>

      <section className="relative mt-8">
        <div className="absolute inset-0">
          <img
            src={IMAGES.gallery[4]}
            alt=""
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.7)' }}
          />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 py-16 md:py-22 text-center text-zb-bg">
          <div className="font-label text-zb-bg/80 mb-6">Your turn?</div>
          <h2 className="font-serif text-4xl md:text-6xl leading-tight">
            The best reviews <span className="italic">are yet to be written.</span>
          </h2>
          <Link
            to="/contact"
            data-testid="testimonials-cta-book"
            className="inline-block mt-10 rounded-full bg-zb-bg hover:bg-zb-brand text-zb-ink hover:text-zb-bg px-10 py-4 text-xs tracking-[0.22em] uppercase transition-colors"
          >
            Write your own
          </Link>
        </div>
      </section>
    </div>
  );
}
